/**
 * One choice of a few, shown whole: a named group of toggle buttons, the chosen one
 * `aria-pressed`. The reports' period presets and the Revenue page's Invoiced | Collected
 * switch both draw through it, side by side in one toolbar, so they share one height, one
 * shape and one way of marking the choice (they had drifted apart as two copies).
 *
 * Real buttons: Tab reaches each, Enter or Space presses it, and the global focus ring shows
 * where the keyboard is. A choice is read from the button's `aria-pressed`, never from its
 * colour alone.
 */
export function PressedGroup<V extends string>({
  label,
  options,
  value,
  onChange,
  busy = false,
}: {
  /** The group's accessible name: what is being chosen ("Period"). */
  label: string;
  options: readonly { value: V; label: string }[];
  value: V;
  onChange: (next: V) => void;
  /**
   * A write this group started is still saving: presses are ignored and every button says
   * `aria-disabled`, never `disabled`, which would drop the focus of the button just pressed to
   * <body> (LESSONS 2026-09-28).
   */
  busy?: boolean;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => {
            if (!busy) onChange(option.value);
          }}
          aria-pressed={value === option.value}
          aria-disabled={busy || undefined}
          className={`min-h-9 rounded-[var(--radius-sm)] px-2.5 text-xs font-medium aria-disabled:opacity-60 ${
            value === option.value
              ? 'bg-surface-2 text-text'
              : 'text-muted-foreground hover:bg-surface-2 hover:text-text'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
